// ============================================================================
// OBT F3 — Nihai park seçim tablosu (aday cevaplar + kararlar)
// ============================================================================
// Aday evreni Müdür kuyruğundan gelir; kararlar karar setinden response_id ile
// eşleştirilir. Karar YALNIZ renkle değil metinle de gösterilir. Kesinleşmiş
// (readOnly) sette karar butonları GÖSTERİLMEZ. Küçük ekranda satırlar kart
// olarak dizilir (yatay taşma yok).
// ============================================================================

import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, XCircle, Clock, MapPin, Trash2, UserPlus } from 'lucide-react';
import { ObtEmptyState } from '../ObtStates';
import DecisionRowDetail from './DecisionRowDetail';
import {
  DECISION_VALUE,
  DECISION_ITEM_LABELS,
  reviewStateLabel,
  isDirectorAddedItem,
} from './reviewEnums';

const TONE = Object.freeze({
  selected: 'bg-green-50 text-green-700 ring-green-200',
  not_selected: 'bg-red-50 text-red-700 ring-red-200',
  postponed: 'bg-amber-50 text-amber-800 ring-amber-200',
  pending: 'bg-gray-100 text-gray-600 ring-gray-300',
});

function DecisionBadge({ decision }) {
  const key = decision || 'pending';
  const label = decision ? DECISION_ITEM_LABELS[decision] : 'Karar bekliyor';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
        TONE[key] || TONE.pending
      }`}
    >
      {label}
    </span>
  );
}

/**
 * @param {object} props
 * @param {Array} props.candidates            director-queue ReviewQueueItem[]
 * @param {Array} props.items                 decisionSet.items
 * @param {boolean} props.readOnly
 * @param {Map<string,string>} [props.nameById]
 * @param {(responseId: string, decision: string, parkName: string, explanation: string) => void} props.onDecide
 * @param {(itemId: string) => void} [props.onRemoveDirectorPark]
 * @param {boolean} [props.actionPending]
 */
export default function FinalParkSelectionTable({
  candidates,
  items,
  readOnly,
  nameById,
  onDecide,
  onRemoveDirectorPark,
  actionPending = false,
}) {
  const decisionByResponse = useMemo(() => {
    const map = new Map();
    for (const it of Array.isArray(items) ? items : []) {
      if (it.response_id != null) map.set(String(it.response_id), it);
    }
    return map;
  }, [items]);

  // Müdürün sonradan eklediği parklar cevap-tabanlı aday değildir; ayrı gösterilir.
  const directorAddedItems = useMemo(
    () => (Array.isArray(items) ? items : []).filter(isDirectorAddedItem),
    [items]
  );

  const rows = Array.isArray(candidates) ? candidates : [];
  if (rows.length === 0 && directorAddedItems.length === 0) {
    return (
      <ObtEmptyState
        title="Aday park yok"
        description="Bu bilgi talebinde müdür kararı bekleyen (MY onaylı) cevap bulunmuyor."
      />
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((c) => {
        const rid = String(c.response_id);
        const item = decisionByResponse.get(rid);
        const decision = item?.decision || null;
        return (
          <Card key={rid}>
            <CardContent className="p-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
                  <span className="text-sm font-semibold text-gray-900">
                    {c.park_name || 'Park'}
                  </span>
                  <DecisionBadge decision={decision} />
                </div>
                <div className="flex flex-wrap items-center gap-3 pl-6 text-xs text-gray-500">
                  <span>
                    Formen:{' '}
                    {nameById?.get(String(c.respondent_personnel_id)) ||
                      'Bilinmeyen personel'}
                  </span>
                  <span>Durum: {reviewStateLabel(c.review_state)}</span>
                </div>
                {item?.explanation && (
                  <p className="pl-6 text-xs text-gray-600">
                    Gerekçe: {item.explanation}
                  </p>
                )}
              </div>

              {!readOnly && (
                <div className="flex flex-shrink-0 flex-wrap gap-1.5">
                  <Button
                    size="sm"
                    variant={decision === DECISION_VALUE.SELECTED ? 'default' : 'outline'}
                    className={
                      decision === DECISION_VALUE.SELECTED
                        ? 'bg-green-600 hover:bg-green-700'
                        : undefined
                    }
                    disabled={actionPending}
                    onClick={() =>
                      onDecide(rid, DECISION_VALUE.SELECTED, c.park_name, item?.explanation)
                    }
                  >
                    <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" /> Dahil Et
                  </Button>
                  <Button
                    size="sm"
                    variant={decision === DECISION_VALUE.NOT_SELECTED ? 'default' : 'outline'}
                    className={
                      decision === DECISION_VALUE.NOT_SELECTED
                        ? 'bg-red-600 hover:bg-red-700'
                        : undefined
                    }
                    disabled={actionPending}
                    onClick={() =>
                      onDecide(
                        rid,
                        DECISION_VALUE.NOT_SELECTED,
                        c.park_name,
                        item?.explanation
                      )
                    }
                  >
                    <XCircle className="mr-1 h-4 w-4" aria-hidden="true" /> Çıkar
                  </Button>
                  <Button
                    size="sm"
                    variant={decision === DECISION_VALUE.POSTPONED ? 'default' : 'outline'}
                    className={
                      decision === DECISION_VALUE.POSTPONED
                        ? 'bg-amber-500 hover:bg-amber-600'
                        : undefined
                    }
                    disabled={actionPending}
                    onClick={() =>
                      onDecide(rid, DECISION_VALUE.POSTPONED, c.park_name, item?.explanation)
                    }
                  >
                    <Clock className="mr-1 h-4 w-4" aria-hidden="true" /> Ertele
                  </Button>
                </div>
              )}
              </div>
              {/* Madde 7 — kararı bağlamla ver: bu cevap neydi, ne oldu. */}
              {c.response_id != null && (
                <DecisionRowDetail responseId={rid} />
              )}
            </CardContent>
          </Card>
        );
      })}

      {directorAddedItems.map((item) => (
        <Card key={`da-${String(item.item_id)}`} className="border-indigo-200 bg-indigo-50/40">
          <CardContent className="flex flex-col gap-2 p-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
                <span className="text-sm font-semibold text-gray-900">
                  {item.park_name || 'Park'}
                </span>
                <DecisionBadge decision={item.decision} />
                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-200">
                  <UserPlus className="h-3 w-3" aria-hidden="true" /> Müdür tarafından
                  eklendi
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 pl-6 text-xs text-gray-500">
                {item.region_name && <span>Bölge: {item.region_name}</span>}
                <span>Cevap bulunmuyor</span>
                <span>İnceleme bulunmuyor</span>
              </div>
              {item.explanation && (
                <p className="pl-6 text-xs text-gray-600">
                  Ekleme gerekçesi: {item.explanation}
                </p>
              )}
            </div>

            {!readOnly && onRemoveDirectorPark && (
              <div className="flex flex-shrink-0 flex-wrap gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  disabled={actionPending}
                  onClick={() => onRemoveDirectorPark(item.item_id)}
                >
                  <Trash2 className="mr-1 h-4 w-4" aria-hidden="true" /> Kaldır
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
